import { useMemo, useState } from "react";
import { Box } from "@mui/material";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { Chip, InlineButton, SingleSelect } from "@coherehealth/common";
import ReorderableChips, { ChipRenderOptions } from "./ReorderableChips";
import { makeStaticOptionsHook } from "./editUserFormOptions";

// Per QM-V2 VQA: every element in a section (chip border/label/delete-icon, drag handle, and the
// "Add {label}" icon + text) uses that section's accent color — blue for skillsets, magenta for
// queues. Only Cancel stays neutral gray.
const CANCEL_COLOR = "#656565";
// Figma 8619:54044: the chip left behind while its copy is being dragged is greyed out.
const GHOST_COLOR = "#c2c6cc";

interface EditableChipListProps {
  /** Singular noun used in the "Add {label}" affordance, e.g. "skillset" / "queue". */
  label: string;
  values: string[];
  onChange: (next: string[]) => void;
  /** Full option catalogue; already-selected values are filtered out of the picker. */
  options: string[];
  /** Skillsets are drag-to-reorder; queues are not. */
  reorderable?: boolean;
  /**
   * Accent color for the outlined chip (border + label + delete icon). Teal for skillsets,
   * magenta for queues — matches the Figma pill styling.
   */
  chipAccentColor: string;
  testIdPrefix: string;
  chipBackGroundColor?: string;
  fontColor?: string;
}

export default function EditableChipList({
  label,
  values,
  onChange,
  options,
  reorderable = false,
  chipAccentColor,
  testIdPrefix,
  chipBackGroundColor,
  fontColor,
}: EditableChipListProps) {
  const [adding, setAdding] = useState(false);
  const [pending, setPending] = useState<string | null>(null);

  const availableOptions = useMemo(() => options.filter((o) => !values.includes(o)), [options, values]);
  const useOptions = useMemo(() => makeStaticOptionsHook(availableOptions), [availableOptions]);

  // Outlined chip (Figma 8619:53585): 32px tall, 1px accent border, 4px radius, 16px/400 label,
  // 8px gap between drag handle / label / 18px clear icon. Non-reorderable chips (queues) have a
  // 16px left inset because they carry no drag handle.
  const chipSx = useMemo(
    () => ({
      display: "inline-flex",
      "& .MuiChip-root": {
        backgroundColor: chipBackGroundColor,
        border: `1px solid ${chipAccentColor}`,
        borderRadius: "4px",
        height: 32,
        paddingLeft: reorderable ? "8px" : "16px",
        paddingRight: "8px",
        fontSize: 16,
        fontWeight: 400,
        lineHeight: 1.2,
      },
      "& .MuiChip-label": { padding: 0, height: "auto", display: "inline-flex", alignItems: "center" },
      "& .MuiChip-deleteIcon": {
        color: `${chipAccentColor} !important`,
        width: "18px !important",
        height: "18px !important",
        marginLeft: "8px !important",
        marginRight: "0 !important",
      },
    }),
    [chipAccentColor, chipBackGroundColor, reorderable]
  );

  const removeAt = (value: string) => onChange(values.filter((v) => v !== value));

  const confirmAdd = () => {
    if (pending && !values.includes(pending)) {
      onChange([...values, pending]);
    }
    setPending(null);
    setAdding(false);
  };

  const cancelAdd = () => {
    setPending(null);
    setAdding(false);
  };

  const renderChip = (value: string, { handleProps, ghost }: ChipRenderOptions = {}) => (
    <Box sx={chipSx}>
      <Chip
        label={
          reorderable ? (
            <Box
              component="span"
              sx={{ display: "inline-flex", alignItems: "center", gap: "8px", color: ghost ? GHOST_COLOR : fontColor }}
            >
              <Box
                component="span"
                {...handleProps}
                sx={{ display: "inline-flex", alignItems: "center", cursor: "grab" }}
                aria-label={`Reorder ${value}`}
              >
                <DragIndicatorIcon style={{ color: ghost ? GHOST_COLOR : fontColor, width: 18, height: 18 }} />
              </Box>
              {value}
            </Box>
          ) : (
            value
          )
        }
        customBackgroundColor={ghost ? "white" : chipBackGroundColor}
        customLabelColor={ghost ? GHOST_COLOR : fontColor ?? chipAccentColor}
        onDelete={() => removeAt(value)}
        data-testid={`${testIdPrefix}-chip-${value}`}
        style={{
          backgroundColor: ghost ? "white" : chipBackGroundColor,
          ...(ghost ? { borderColor: GHOST_COLOR, color: GHOST_COLOR } : {}),
        }}
      />
    </Box>
  );

  return (
    <Box data-testid={`${testIdPrefix}-list`}>
      {reorderable ? (
        <ReorderableChips values={values} onChange={onChange} renderChip={renderChip} />
      ) : (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center" }}>
          {values.map((value) => (
            <Box key={value}>{renderChip(value)}</Box>
          ))}
        </Box>
      )}

      {adding ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
          <Box sx={{ minWidth: 280 }}>
            <SingleSelect<string, false>
              useOptions={useOptions}
              selectedValue={pending}
              setSelectedValue={(value) => setPending((value as string) ?? null)}
              emptyLabel={`Select ${label}`}
              withDropdownArrow
              markSelectedOptions={false}
              data-testid={`${testIdPrefix}-add-select`}
            />
          </Box>
          <InlineButton
            onClick={confirmAdd}
            disabled={!pending}
            style={{ color: chipAccentColor }}
            data-testid={`${testIdPrefix}-add-confirm`}
          >
            Add
          </InlineButton>
          <InlineButton onClick={cancelAdd} style={{ color: CANCEL_COLOR }} data-testid={`${testIdPrefix}-add-cancel`}>
            Cancel
          </InlineButton>
        </Box>
      ) : (
        // Figma "Main Button": 24px icon in the accent color, 14px/600 label, 4px gap.
        <InlineButton
          startIcon={<AddCircleIcon style={{ color: chipAccentColor, width: 24, height: 24 }} />}
          onClick={() => setAdding(true)}
          data-testid={`${testIdPrefix}-add-btn`}
          style={{
            marginTop: 8,
            color: fontColor ?? chipAccentColor,
            fontSize: 14,
            fontWeight: 600,
            lineHeight: 1.2,
            padding: 0,
            minWidth: 0,
          }}
        >
          {`Add ${label}`}
        </InlineButton>
      )}
    </Box>
  );
}
